import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Loader2, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";

export default function ApprovalQueue() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState("");

  const fetchQueue = async () => {
    try {
      const res = await api.get("/admin/approval-queue");
      setItems(res.data.requests || []);
    } catch {
      toast.error("Failed to load approval queue");
    }
    setLoading(false);
  };

  useEffect(() => { fetchQueue(); }, []);

  const decide = async (requestId, decision) => {
    try {
      await api.post(`/admin/approval-queue/${requestId}/decision`, { decision, comment });
      toast.success(`Request ${decision}d`);
      setComment("");
      fetchQueue();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Decision failed");
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-10 space-y-6" data-testid="approval-queue-page">
      <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight flex items-center gap-3"><ClipboardCheck className="w-8 h-8 text-court-orange" />Approval Queue</h1>
      <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Decision comment (required for reject / return)" data-testid="approval-queue-comment" />
      <div className="grid grid-cols-1 gap-3">
        {items.map((item) => (
          <Card key={item.request_id} data-testid={`approval-request-${item.request_id}`}>
            <CardHeader><CardTitle className="font-heading text-lg flex items-center justify-between gap-3"><span>{item.title}</span><Badge variant="outline">{item.status}</Badge></CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-zinc-600">{item.module} · {item.target_type} · {item.target_id || 'pending target'}</p>
              <p className="text-sm text-zinc-600">Maker: {item.maker_name} ({item.maker_email})</p>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => decide(item.request_id, "approve")} data-testid={`approve-request-${item.request_id}`}>Approve</Button>
                <Button size="sm" variant="outline" onClick={() => decide(item.request_id, "reject")} data-testid={`reject-request-${item.request_id}`}>Reject</Button>
                <Button size="sm" variant="outline" onClick={() => decide(item.request_id, "return")} data-testid={`return-request-${item.request_id}`}>Return</Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}